import { useState, useRef } from "react";
import { Star, X, Upload, Loader2, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolve = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u);

export const ReviewForm = ({ open, onClose, onSubmitted }) => {
  const [form, setForm] = useState({ name: "", location: "", rating: 5, title: "", text: "" });
  const [photo, setPhoto] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/reviews/upload-photo", fd);
      setPhoto(r.data.url);
    } catch {
      toast.error("Photo upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!form.name.trim() || !form.title.trim() || !form.text.trim()) {
      toast.error("Please add your name, a title and your review");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/reviews", { ...form, photo });
      toast.success("Thank you! Your review is now live.");
      onSubmitted?.(r.data);
      onClose?.();
      setForm({ name: "", location: "", rating: 5, title: "", text: "" });
      setPhoto("");
    } catch {
      toast.error("Could not submit your review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" data-testid="review-form-modal" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-[#FFFCFA] p-7 md:p-9" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-[#AFA295]">Share Your Glow</p>
            <h3 className="mt-1 font-display text-2xl text-[#2C2B2C]">Write a review</h3>
          </div>
          <button onClick={onClose} data-testid="review-form-close" className="rounded-full p-1.5 text-[#8A7F86] hover:bg-[#F2E8E6]"><X className="h-5 w-5" /></button>
        </div>

        <div className="mt-6 space-y-4">
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} onClick={() => set("rating", n)} data-testid={`review-star-${n}`}>
                <Star className={`h-7 w-7 transition-colors ${n <= form.rating ? "fill-[#F4B999] text-[#F4B999]" : "text-[#E5DBC8]"}`} />
              </button>
            ))}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name"
              data-testid="review-name" className="rounded-xl border border-[#E5DBC8] bg-white px-4 py-3 text-sm text-[#2C2B2C] outline-none focus:border-[#FBC4A8]" />
            <input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Location (optional)"
              data-testid="review-location" className="rounded-xl border border-[#E5DBC8] bg-white px-4 py-3 text-sm text-[#2C2B2C] outline-none focus:border-[#FBC4A8]" />
          </div>

          <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Review title"
            data-testid="review-title" className="w-full rounded-xl border border-[#E5DBC8] bg-white px-4 py-3 text-sm text-[#2C2B2C] outline-none focus:border-[#FBC4A8]" />

          <textarea value={form.text} onChange={(e) => set("text", e.target.value)} placeholder="Tell us about your experience..." rows={4}
            data-testid="review-text" className="w-full resize-none rounded-xl border border-[#E5DBC8] bg-white px-4 py-3 text-sm text-[#2C2B2C] outline-none focus:border-[#FBC4A8]" />

          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
            data-testid="review-photo-input" onChange={(e) => onPhoto(e.target.files?.[0])} />

          {photo ? (
            <div className="relative w-fit">
              <img src={resolve(photo)} alt="Your upload" className="h-28 w-28 rounded-xl object-cover" />
              <button onClick={() => setPhoto("")} className="absolute -right-2 -top-2 rounded-full bg-[#232222] p-1 text-white"><X className="h-3.5 w-3.5" /></button>
            </div>
          ) : (
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="review-add-photo"
              className="flex items-center gap-2 rounded-xl border border-dashed border-[#E5DBC8] px-4 py-3 text-sm text-[#8A7F86] hover:border-[#FBC4A8]">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} Add a photo (optional)
            </button>
          )}

          <button onClick={submit} disabled={submitting} data-testid="review-submit"
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-[#232222] py-4 text-xs font-semibold uppercase tracking-[0.14em] text-[#FFFCFA] transition-all hover:bg-[#4A2A38] disabled:opacity-60">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Submit review
          </button>
        </div>
      </div>
    </div>
  );
};
